import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Recipe } from '../../models/Recipe';
import { getRecipeThumbnail } from '../../utils/RecipeService';

type Props = {
  matches: Recipe[];
  currentUsername: string;
  partnerName: string;
  onNewSession: () => void;
};

function formatCookTime(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

const MatchRow = ({ recipe, currentUsername, partnerName, onClick }: {
  recipe: Recipe; currentUsername: string; partnerName: string; onClick: () => void;
}) => {
  const [thumbnail, setThumbnail] = useState<string>();

  useEffect(() => {
    if (recipe.id != null) {
      getRecipeThumbnail(recipe.id).then(blob => {
        if (blob) setThumbnail(URL.createObjectURL(blob));
      });
    }
  }, [recipe.id]);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        border: '1.5px solid', borderColor: 'divider',
        borderRadius: 3, p: 1, cursor: 'pointer',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{
        width: 48, height: 48, borderRadius: 2, flexShrink: 0,
        backgroundImage: thumbnail ? `url(${thumbnail})` : undefined,
        backgroundSize: 'cover', backgroundPosition: 'center',
        bgcolor: thumbnail ? undefined : 'grey.100',
      }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={600} fontSize="0.85rem" noWrap>{recipe.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
            <AccessTimeIcon sx={{ fontSize: 11 }} />
            <Typography variant="caption">{formatCookTime(recipe.cookTime)}</Typography>
          </Box>
          {recipe.tags && recipe.tags.length > 0 && (
            <Typography variant="caption" color="text.secondary">· {recipe.tags[0]}</Typography>
          )}
        </Box>
      </Box>

      {/* Stacked avatars */}
      <Box sx={{ display: 'flex', mr: 0.5 }}>
        {[currentUsername, partnerName].map((name, i) => (
          <Box key={name} sx={{
            width: 20, height: 20, borderRadius: '50%',
            border: '1.5px solid white', bgcolor: 'grey.300',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.58rem', color: 'text.secondary', fontWeight: 600,
            ml: i > 0 ? -0.875 : 0,
          }}>
            {name.charAt(0).toUpperCase()}
          </Box>
        ))}
      </Box>

      <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 18, flexShrink: 0 }} />
    </Box>
  );
};

const RecipeMatchList = ({ matches, currentUsername, partnerName, onNewSession }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, px: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, flexShrink: 0 }}>
        <Typography variant="h5" fontWeight={700}>{t('match_sharedMatches')}</Typography>
        <Box sx={{
          minWidth: 22, height: 22, px: 0.75, borderRadius: '11px',
          bgcolor: 'primary.main', color: '#fff',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 700,
        }}>
          {matches.length}
        </Box>
      </Box>

      {matches.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary" textAlign="center">
            {t('match_noMatches')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25, overflowY: 'auto', pb: 1 }}>
          {matches.map(recipe => (
            <MatchRow
              key={recipe.id}
              recipe={recipe}
              currentUsername={currentUsername}
              partnerName={partnerName}
              onClick={() => navigate('/recipe/' + recipe.id)}
            />
          ))}
        </Box>
      )}

      <Button
        variant="contained"
        size="large"
        onClick={() => navigate('/recipePlan')}
        sx={{ borderRadius: 8, py: 1.4, fontSize: '1rem', fontWeight: 700, mt: 2, mb: 1, flexShrink: 0 }}
      >
        {t('match_planWeek')}
      </Button>
      <Button
        variant="outlined"
        size="large"
        onClick={onNewSession}
        sx={{ borderRadius: 8, py: 1.3, fontSize: '1rem', borderWidth: 2, mb: 2, flexShrink: 0, '&:hover': { borderWidth: 2 } }}
      >
        {t('match_again')}
      </Button>
    </Box>
  );
};

export default RecipeMatchList;
